import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Play, FileText } from 'lucide-react';

export default function ProductModal({ product, lang, onClose }) {
  const [currentImage, setCurrentImage] = useState(0);

  if (!product) return null;

  const images = product.images || [product.imageUrl];
  const name = product.name?.[lang] || product.name?.en || "Product";
  const desc = product.description?.[lang] || product.description?.en || "";
  const hasVideo = !!product.videoUrl;

  const nextImage = () => setCurrentImage((prev) => (prev + 1) % images.length);
  const prevImage = () => setCurrentImage((prev) => (prev - 1 + images.length) % images.length);

  const getYouTubeThumbnail = (url) => {
    const match = url?.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
    return match ? `https://img.youtube.com/vi/${match[1]}/mqdefault.jpg` : null;
  };

  const getYouTubeEmbedUrl = (url) => {
    const match = url?.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
    return match ? `https://www.youtube.com/watch?v=${match[1]}` : url;
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="bg-white rounded-2xl max-w-4xl w-full max-h-[92vh] overflow-y-auto shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="sticky top-0 bg-white/95 backdrop-blur-sm z-10 px-6 py-4 border-b flex items-center justify-between rounded-t-2xl">
            <h2 className="text-2xl font-bold text-brand-blue">{name}</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition">
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="p-6">
            <div className="relative mb-6 bg-gray-50 rounded-xl overflow-hidden">
              <div className="h-80 md:h-96 flex items-center justify-center relative">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={currentImage}
                    src={images[currentImage]}
                    alt={`${name} - ${currentImage + 1}`}
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -50 }}
                    transition={{ duration: 0.3 }}
                    className="max-h-full max-w-full object-contain p-4"
                  />
                </AnimatePresence>
              </div>

              {images.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-3 top-1/2 -translate-y-1/2 p-2 bg-white/90 rounded-full shadow hover:bg-white transition"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-2 bg-white/90 rounded-full shadow hover:bg-white transition"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
                    {images.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setCurrentImage(i)}
                        className={`w-2.5 h-2.5 rounded-full transition ${
                          i === currentImage ? "bg-brand-blue" : "bg-gray-300"
                        }`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="flex gap-3 mb-4 overflow-x-auto pb-2">
              {images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentImage(i)}
                  className={`flex-shrink-0 w-20 h-20 rounded-lg border-2 overflow-hidden ${
                    i === currentImage ? "border-brand-blue" : "border-gray-200"
                  }`}
                >
                  <img src={img} alt="" className="w-full h-full object-contain bg-gray-50 p-1" />
                </button>
              ))}

              {hasVideo && (
                <a
                  href={getYouTubeEmbedUrl(product.videoUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-shrink-0 w-20 h-20 rounded-lg border-2 border-gray-200 hover:border-red-500 overflow-hidden relative group"
                >
                  <img
                    src={getYouTubeThumbnail(product.videoUrl)}
                    alt="Video"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 flex flex-col items-center justify-center transition">
                    <Play className="w-6 h-6 text-white fill-current mb-1" />
                    <span className="text-white text-[10px] font-bold">YOUTUBE</span>
                  </div>
                </a>
              )}
            </div>

            <div className="flex flex-wrap gap-3 mb-4">
              <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
                {product.category}
              </span>
              {product.application && (
                <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm font-medium capitalize">
                  {product.application}
                </span>
              )}
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Beschreibung</h3>
              <p className="text-gray-600 leading-relaxed">{desc}</p>
            </div>

            {product.specs && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Spezifikationen</h3>
                <div className="flex flex-wrap gap-2">
                  {product.specs.split("|").map((spec, i) => (
                    <span
                      key={i}
                      className="px-3 py-1.5 bg-gray-50 border border-gray-200 text-gray-700 rounded-lg text-sm font-mono"
                    >
                      {spec.trim()}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center justify-between pt-4 border-t">
              <div>
                <span className="text-sm text-gray-500">Produkt-ID</span>
                <p className="font-mono text-gray-700">{product.id}</p>
              </div>
              <div className="text-right">
                <span className="text-sm text-gray-500">Preis</span>
                <p className="text-xl font-bold text-brand-blue">
                  {product.price ? `€${product.price}` : "Preis auf Anfrage"}
                </p>
              </div>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              {hasVideo && (
                <a
                  href={getYouTubeEmbedUrl(product.videoUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-medium"
                >
                  <Play className="w-4 h-4 fill-current" />
                  Video auf YouTube ansehen
                </a>
              )}

              {product.datasheetUrl && (
                <a
                  href={product.datasheetUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-white text-brand-blue border border-brand-blue rounded-lg hover:bg-blue-50 transition font-medium"
                >
                  <FileText className="w-4 h-4" />
                  Datasheet PDF
                </a>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
